import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import { FitViewOptions, ReactFlowState, useReactFlow, useStore } from 'reactflow';

import { useDagContext } from './DagProvider';

const MIN_ZOOM_SCALAR = 0.04;
const MAX_ZOOM_SCALAR = 1.6;

export interface ViewportOptions {
  padding: number | undefined;
  xOffset: number | undefined;
  yOffset: number | undefined;
  zoom: number | undefined;
}

type ViewportContextType = {
  onResize?: () => void;
  resetView: (instantReset?: boolean) => void;
  recenterView: () => void;
  onInteraction: () => void;
  setNodesToFit: (nodes: { id: string }[] | null) => void;
  fitViewOptions: FitViewOptions | undefined;
  padding: number;
  xOffset: number;
  yOffset: number;
  minZoom: number;
  maxZoom: number;
};

export const ViewportContext = createContext<ViewportContextType>({
  onResize: undefined,
  resetView: () => undefined,
  recenterView: () => undefined,
  onInteraction: () => undefined,
  setNodesToFit: () => undefined,
  fitViewOptions: undefined,
  padding: 0,
  xOffset: 0,
  yOffset: 0,
  minZoom: 0,
  maxZoom: 0,
});

export const useViewportContext = () => {
  return useContext(ViewportContext);
};

interface ViewportProviderProps {
  padding: number | undefined;
  xOffset: number | undefined;
  yOffset: number | undefined;
  resetZoomOnResize: boolean | undefined;
  minZoom: number | undefined;
  maxZoom: number | undefined;
}

const DIMENSIONS_DEBOUNCE_MILLIS = 250;

function getStoreWidth(state: ReactFlowState) {
  return state.width;
}

function useReactFlowWidth() {
  const rawWidth = useStore(getStoreWidth);
  const [width, setWidth] = useState(rawWidth);
  useDebounce(
    () => {
      setWidth(rawWidth);
    },
    DIMENSIONS_DEBOUNCE_MILLIS,
    [rawWidth],
  );
  return width;
}

function getStoreHeight(state: ReactFlowState) {
  return state.height;
}

function useReactFlowHeight() {
  const rawHeight = useStore(getStoreHeight);
  const [height, setHeight] = useState(rawHeight);
  useDebounce(
    () => {
      setHeight(rawHeight);
    },
    DIMENSIONS_DEBOUNCE_MILLIS,
    [rawHeight],
  );
  return height;
}

const useMonitorResetView = ({
  resetViewOnResize,
  viewport,
}: {
  resetViewOnResize: boolean;
  viewport: ViewportContextType;
}) => {
  const resetViewOnResizeRef = useRef(resetViewOnResize);
  resetViewOnResizeRef.current = resetViewOnResize;

  const reactFlowWidth = useReactFlowWidth();
  const reactFlowHeight = useReactFlowHeight();

  const onContainerResize = useCallback(() => {
    if (resetViewOnResizeRef.current) {
      viewport.resetView();
    } else {
      viewport.recenterView();
    }
    viewport.onResize?.();
  }, [viewport]);

  // FIXME: define correct deps array
  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onContainerResize, [
    viewport.padding,
    viewport.xOffset,
    viewport.yOffset,
    reactFlowWidth,
    reactFlowHeight,
  ]);
};

export const ViewportProvider: FC<PropsWithChildren<ViewportProviderProps>> = ({
  xOffset = 0,
  yOffset = 0,
  padding = 0.05,
  minZoom = MIN_ZOOM_SCALAR,
  maxZoom = MAX_ZOOM_SCALAR,
  resetZoomOnResize = true,
  children,
}) => {
  const reactFlowWidth = useReactFlowWidth();
  const reactFlowHeight = useReactFlowHeight();

  const reactFlowLastWidthRef = useRef(reactFlowWidth);
  const reactFlowLastHeightRef = useRef(reactFlowHeight);

  const lastInteractionRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [nodesToFit, setNodesToFit] = useState<{ id: string }[] | null>(null);
  const [fitViewOptions, setFitViewOptions] = useState<FitViewOptions | undefined>();
  useEffect(() => {
    const newOptions = {
      maxZoom: Math.min(maxZoom, 1.0),
      minZoom,
      nodes: nodesToFit ?? [],
      padding,
    };
    JSON.stringify(newOptions) !== JSON.stringify(fitViewOptions) &&
      setFitViewOptions(newOptions);
  }, [fitViewOptions, maxZoom, minZoom, nodesToFit, padding]);

  const { fitView, setViewport, getViewport } = useReactFlow();
  const dagContext = useDagContext();

  const viewport = useMemo(() => {
    const onInteraction = () => {
      lastInteractionRef.current = Date.now();
      dagContext.hideContextMenu();
    };

    const resetView = (instantReset?: boolean) => {
      if (instantReset) {
        lastInteractionRef.current = 0;
        clearTimeout(timeoutRef.current);
      }
      fitView(fitViewOptions);
    };

    const recenterView = () => {
      const { x, y, zoom: currentZoom } = getViewport();
      const xDiff = (reactFlowWidth - reactFlowLastWidthRef.current) / 2;
      const yDiff = (reactFlowHeight - reactFlowLastHeightRef.current) / 2;

      const w = reactFlowLastWidthRef.current;
      const h = reactFlowLastHeightRef.current;

      const xScalar = w ? xDiff / w : 0;
      const yScalar = h ? yDiff / h : 0;
      const scalar = Math.abs(xScalar) > Math.abs(yScalar) ? xScalar : yScalar;
      const zoomScalar = 1 + scalar;
      if (!reactFlowLastWidthRef.current || !reactFlowLastWidthRef.current) return;
      const viewport = {
        x: x + xDiff / 2,
        y: y + yDiff / 2,
        zoom: currentZoom * zoomScalar,
      };
      if (
        Number.isNaN(viewport.x) ||
        Number.isNaN(viewport.y) ||
        Number.isNaN(viewport.zoom)
      )
        return;

      setViewport(viewport);
    };

    const onResize = () => {
      reactFlowLastWidthRef.current = reactFlowWidth;
      reactFlowLastHeightRef.current = reactFlowHeight;
    };

    return {
      /**
       * Center and re-zoom content to fill the full DAG.
       */
      resetView,
      /**
       * Center content to attempt to keep the same
       * contents on screen.
       */
      recenterView,
      /**
       * In order to avoid moving the viewport while users are interacting,
       * you you should call `onInteraction` anywhere you would expect the viewport
       * to be stable for a short period of time.
       */
      onInteraction,
      /**
       * Should be called when viewport resizes
       */
      onResize,
      /**
       * Updates which set of nodes we should focus on fitting to the viewport.
       */
      setNodesToFit,
      fitViewOptions,
      xOffset,
      yOffset,
      padding,
      maxZoom,
      minZoom,
    };
  }, [
    dagContext,
    fitView,
    fitViewOptions,
    getViewport,
    maxZoom,
    minZoom,
    padding,
    reactFlowHeight,
    reactFlowWidth,
    setViewport,
    xOffset,
    yOffset,
  ]);

  useMonitorResetView({
    resetViewOnResize: resetZoomOnResize,
    viewport,
  });

  return (
    <ViewportContext.Provider value={viewport}>{children}</ViewportContext.Provider>
  );
};
